import { TBasicInputInstance } from 'widgets/forms/BasicInput';

const DEFAULT_ALLOWED_PATTERN = /^[A-Za-z0-9]+$/;

export type TPersonalizationRules = {
    maxLength: number;
    pattern: string;
    blockedWords: Array<string>;
};

/**
 * @description Validate the personalization text.
 * @param text - personalization text
 * @param rules - validation rules
 * @returns error type or null if the text is valid
 */
export function validatePersonalizationText(text: string, rules: TPersonalizationRules): string | null {
    const value = text || '';

    if (!value) {
        return 'required';
    }

    if (value.length > rules.maxLength) {
        return 'maxLength';
    }

    let pattern: RegExp;

    try {
        pattern = rules.pattern ? new RegExp(rules.pattern) : DEFAULT_ALLOWED_PATTERN;
    } catch {
        pattern = DEFAULT_ALLOWED_PATTERN;
    }

    if (!pattern.test(value)) {
        return 'invalid';
    }

    return (rules.blockedWords || []).includes(value.toUpperCase()) ? 'blocked' : null;
}

/**
 * @description PersonalizationDrawer implementation
 * @param Modal Base widget for extending
 * @returns Personalization drawer class
 */
function PersonalizationDrawerClassCreator(Modal: import('widgets/global/Modal').TModal) {
    class PersonalizationDrawer extends Modal {
        isImagesRendered = false;

        /**
         * Validation rules of the product, set when the drawer opens
         */
        rules: TPersonalizationRules = { maxLength: 0, pattern: '', blockedWords: [] };

        prefs() {
            return {
                images: <Array<Record<string, unknown>>>[],
                textErrorRequired: '',
                textErrorMaxLength: '',
                textErrorInvalid: '',
                textErrorBlocked: '',
                textErrorTerms: '',
                ...super.prefs()
            };
        }

        /**
         * @description Get the personalization text input widget
         * @returns input widget
         */
        getTextInput(): TBasicInputInstance | undefined {
            return this.getById<TBasicInputInstance, TBasicInputInstance>('personalizationText', input => input);
        }

        /**
         * @description Open the drawer filled with the active personalization
         * @param text - active personalization text, empty if the product is not personalized yet
         * @param rules - validation rules of the product
         */
        async openDrawer(text: string, rules: TPersonalizationRules): Promise<void> {
            const textInput = this.getTextInput();

            this.rules = rules;

            textInput?.setValue(text, true);
            textInput?.callIfExists('checkValue');
            this.ref('terms').prop('checked', !!text);

            this.toggleTextError(null);
            this.toggleTermsError(false);
            this.updateCounter();

            await this.renderImages();
            await this.showModal();
        }

        /**
         * @description Set the images of the selected product, they are rendered the next time the drawer opens
         * @param images - images of the `personalization` view type
         */
        setImages(images: Array<Record<string, unknown>>): void {
            this.setPref('images', images);
            this.isImagesRendered = false;
        }

        /**
         * @description Render the images once per selected product. The container is emptied first,
         * so the carousel widget is created again instead of being patched
         * @returns rendering promise
         */
        renderImages(): Promise<void> {
            if (this.isImagesRendered) {
                return Promise.resolve();
            }

            const images = this.prefs().images || [];

            this.isImagesRendered = true;
            this.ref('images').empty();

            return this.render(
                'imagesTemplate',
                {
                    images: images,
                    isCarousel: images.length > 1
                },
                this.ref('images')
            );
        }

        /**
         * @description Get the entered text
         * @returns text without leading and trailing spaces
         */
        getText(): string {
            return String(this.getTextInput()?.getValue() || '').trim();
        }

        /**
         * @description Check whether the terms are accepted
         * @returns true if the terms checkbox is checked
         */
        isTermsAccepted(): boolean {
            return !!this.ref('terms').prop('checked');
        }

        /**
         * @description Validate the text against the site rules
         * @returns error type or null if the text is valid
         */
        validateText(): string | null {
            return validatePersonalizationText(this.getText(), this.rules);
        }

        /**
         * @description Text input handler: updates the counter and shows the errors that the user can fix while typing
         */
        onTextInput(): void {
            this.updateCounter();

            const error = this.validateText();

            this.toggleTextError(error === 'required' ? null : error);
        }

        /**
         * @description Terms checkbox handler
         */
        onTermsChange(): void {
            if (this.isTermsAccepted()) {
                this.toggleTermsError(false);
            }
        }

        /**
         * @description Update the `entered/max` characters counter
         */
        updateCounter(): void {
            this.ref('counter').setText(`${this.getText().length}/${this.rules.maxLength}`);
        }

        /**
         * @description Show or hide the text error
         * @param error - error type, null to hide the error
         */
        toggleTextError(error: string | null): void {
            const messages: Record<string, string> = {
                required: this.prefs().textErrorRequired,
                maxLength: this.prefs().textErrorMaxLength,
                invalid: this.prefs().textErrorInvalid,
                blocked: this.prefs().textErrorBlocked
            };

            const input = this.getTextInput();

            if (input) {
                input.ref('self').toggleClass('m-invalid', !!error);
                input
                    .ref('field')
                    .toggleClass('m-invalid', !!error)
                    .attr('aria-invalid', error ? 'true' : 'false');
            }

            this.ref('textErrorMessage').setText(error ? messages[error] : '');
            this.ref('textError').toggle(!!error);
        }

        /**
         * @description Show or hide the terms error
         * @param show - show the error
         */
        toggleTermsError(show: boolean): void {
            this.ref('termsWrapper').toggleClass('m-invalid', show);
            this.ref('terms').attr('aria-invalid', show ? 'true' : 'false');
            this.ref('termsError').toggle(show);
        }

        /**
         * @description Save handler: validates the form and emits the valid text
         * @emits PersonalizationDrawer#save
         */
        save(): void {
            const textError = this.validateText();
            const isTermsAccepted = this.isTermsAccepted();

            this.toggleTextError(textError);
            this.toggleTermsError(!isTermsAccepted);

            if (textError) {
                this.getTextInput()?.focus();

                return;
            }

            if (!isTermsAccepted) {
                this.ref('terms').focus();

                return;
            }

            this.closeModal();

            /**
             * @description Event dispatched with the valid personalization text
             * @event PersonalizationDrawer#save
             */
            this.emit('save', this.getText());
        }
    }

    return PersonalizationDrawer;
}

export type TPersonalizationDrawer = ReturnType<typeof PersonalizationDrawerClassCreator>;

export type TPersonalizationDrawerInstance = InstanceType<TPersonalizationDrawer>;

export default PersonalizationDrawerClassCreator;
